import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Link,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Button,
  Tooltip
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';

const typeIcon = (type) => {
  const iconProps = { fontSize: 'small' };
  switch (type?.toLowerCase()) {
    case 'auto': return <DirectionsCarIcon {...iconProps} />;
    case 'home': return <HomeIcon {...iconProps} />;
    case 'business': return <BusinessIcon {...iconProps} />;
    case 'life': return <FavoriteIcon {...iconProps} />;
    default: return <Avatar {...iconProps} />;
  }
};

const getTypeColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'auto': return '#2196f3';
    case 'home': return '#ff9800';
    case 'business': return '#4caf50';
    case 'life': return '#f44336';
    default: return '#9e9e9e';
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'new': return 'success';
    case 'contacted': return 'info';
    case 'follow-up': return 'warning';
    case 'quoted': return 'secondary';
    default: return 'default';
  }
};

const LeadItem = ({ lead, onAction }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Get primary insurance type for display
  const primaryInsurance = lead.insuranceTypes && lead.insuranceTypes.length > 0 
    ? lead.insuranceTypes[0] 
    : { type: 'auto' };
  
  const insuranceType = typeof primaryInsurance === 'string' ? primaryInsurance : primaryInsurance.type;
  const typeColor = getTypeColor(insuranceType);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    onAction(lead._id || lead.id, action);
    handleMenuClose();
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const createdDate = new Date(date);
    const diffInMs = now - createdDate;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return `${diffInDays}d ago`;
    }
  };

  return (
    <>
      <ListItem
        alignItems="flex-start"
        sx={{
          px: 2,
          py: 2,
          '&:hover': { 
            backgroundColor: 'rgba(0,0,0,0.03)',
            transform: 'translateX(4px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          },
          borderRadius: 2,
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          border: '1px solid transparent',
          '&:hover': {
            border: `1px solid ${typeColor}20`,
            backgroundColor: `${typeColor}05`
          }
        }}
      >
        <ListItemAvatar sx={{ minWidth: 56 }}>
          <Avatar sx={{ 
            bgcolor: `${typeColor}15`,
            color: typeColor,
            width: 48,
            height: 48,
            boxShadow: `0 3px 8px ${typeColor}20`,
            border: `2px solid ${typeColor}30`
          }}>
            {typeIcon(insuranceType)}
          </Avatar>
        </ListItemAvatar>
        
        <ListItemText
          sx={{ pr: 1 }}
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
              <Link 
                href="#" 
                underline="hover" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '1rem', 
                  color: '#1a1a1a',
                  '&:hover': { color: typeColor }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  onAction(lead._id || lead.id, 'view');
                }}
              >
                {lead.visitorName || 'Unknown'}
              </Link>
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ 
                display: 'block', 
                mb: 0.5,
                fontWeight: 500,
                fontSize: '0.85rem'
              }}>
                {lead.email || 'No email'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  fontWeight: 500
                }}>
                  <AccessTimeIcon sx={{ fontSize: 12 }} />
                  {formatTimeAgo(lead.createdAt)}
                </Typography>
                <Chip
                  label={insuranceType ? insuranceType.charAt(0).toUpperCase() + insuranceType.slice(1) : 'General'}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    height: 18, 
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    borderColor: typeColor,
                    color: typeColor,
                    backgroundColor: `${typeColor}08`
                  }}
                />
              </Box>
            </Box>
          }
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Call" placement="top">
            <IconButton 
              size="small" 
              onClick={() => handleAction('call')}
              sx={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                '&:hover': { 
                  backgroundColor: '#1976d2',
                  color: 'white',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <PhoneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Email" placement="top">
            <IconButton 
              size="small" 
              onClick={() => handleAction('email')}
              sx={{
                backgroundColor: '#e8f5e8',
                color: '#388e3c',
                '&:hover': { 
                  backgroundColor: '#388e3c',
                  color: 'white',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
         
        </Box>
      </ListItem>
    </>
  );
};

export default function LeadsTable({ selectedEvent, submissions = [], sx }) {
  const [tab, setTab] = useState(0);
  
  // Process real submission data
  const processedLeads = useMemo(() => {
    return submissions.map(submission => ({
      ...submission,
      id: submission._id || submission.id,
      expiring: false // You can add logic here to determine if a lead is expiring
    }));
  }, [submissions]);

  const newLeads = useMemo(() => {
    const today = new Date();
    const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);
    
    return processedLeads.filter(lead => {
      const createdDate = new Date(lead.createdAt);
      return createdDate > threeDaysAgo && lead.status === 'New';
    });
  }, [processedLeads]);

  const expiringLeads = useMemo(() => {
    return processedLeads.filter(lead => {
      // Check if any insurance types have renewal dates in the next 30 days
      if (!lead.insuranceTypes || !Array.isArray(lead.insuranceTypes)) return false;
      
      const today = new Date();
      const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      return lead.insuranceTypes.some(insurance => {
        if (!insurance.renewalDate) return false;
        const renewalDate = new Date(insurance.renewalDate);
        return renewalDate <= thirtyDaysFromNow && renewalDate >= today;
      });
    });
  }, [processedLeads]);

  const currentLeads = tab === 0 ? newLeads : expiringLeads;

  const handleLeadAction = (leadId, action) => {
    console.log(`Action ${action} on lead ${leadId}`);
    
    switch (action) {
      case 'call':
        const lead = processedLeads.find(l => (l._id || l.id) === leadId);
        if (lead?.phoneNumber) {
          window.open(`tel:${lead.phoneNumber}`);
        }
        break;
      case 'email':
        const emailLead = processedLeads.find(l => (l._id || l.id) === leadId);
        if (emailLead?.email) {
          window.open(`mailto:${emailLead.email}?subject=Insurance Inquiry Follow-up`);
        }
        break;
      case 'view':
        // Navigate to submissions page with this lead selected
        // You could implement this navigation
        console.log('Navigate to lead details');
        break;
      default:
        // Handle other actions
        break;
    }
  };

  return (
    <Card sx={{ 
      height: '100%',
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid rgba(0,0,0,0.06)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: 'linear-gradient(90deg, #2196f3, #ff9800, #4caf50, #f44336)'
      },
      ...sx
    }}>
      <CardContent sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column' }}>
        {/* Enhanced Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar sx={{
            bgcolor: '#6366f1',
            width: 48,
            height: 48,
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
          }}>
            <PeopleIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 0.5 }}>
              Recent Leads
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {currentLeads.length} active leads
            </Typography>
          </Box>
        </Box>

        {/* Enhanced Tabs */}
        <Tabs 
          value={tab} 
          onChange={(_, v) => setTab(v)} 
          sx={{ 
            mb: 2, 
            minHeight: 'auto',
            '& .MuiTab-root': {
              fontWeight: 600,
              borderRadius: 2,
              mx: 0.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                color: '#6366f1'
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
          variant="fullWidth"
        >
          <Tab 
            label={`New Leads (${newLeads.length})`}
            sx={{ minHeight: 'auto', py: 1.5 }}
          />
          <Tab 
            label={`Expiring (${expiringLeads.length})`}
            sx={{ minHeight: 'auto', py: 1.5 }}
          />
        </Tabs>

        {/* Leads List */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: 6
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderRadius: 3
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 3,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)'
            }
          }
        }}>
          <List sx={{ p: 0 }}>
            {currentLeads.map((lead, i) => (
              <React.Fragment key={lead._id || lead.id}>
                <LeadItem lead={lead} onAction={handleLeadAction} />
                {i < currentLeads.length - 1 && <Box sx={{ height: 8 }} />}
              </React.Fragment>
            ))}
            {currentLeads.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No leads found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {tab === 0 ? 'No new leads in the last 3 days' : 'No leads with upcoming renewals'}
                </Typography>
                <Button 
                  variant="contained" 
                  size="medium"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    backgroundColor: '#6366f1',
                    '&:hover': {
                      backgroundColor: '#5856eb'
                    }
                  }}
                  onClick={() => window.open('/admin/submissions', '_blank')}
                >
                  View All Submissions
                </Button>
              </Box>
            )}
          </List>
        </Box>

        {/* Enhanced Footer */}
        {currentLeads.length > 0 && (
          <Box sx={{ 
            mt: 2, 
            pt: 2, 
            borderTop: '1px solid rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            mx: -3,
            mb: -3,
            px: 3,
            py: 2,
            borderRadius: '0 0 12px 12px'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total: {currentLeads.length} leads
              </Typography>
              <Button 
                size="small" 
                variant="contained"
                sx={{
                  borderRadius: 2,
                  px: 2,
                  backgroundColor: '#6366f1',
                  '&:hover': {
                    backgroundColor: '#5856eb'
                  }
                }}
                onClick={() => window.open('/admin/submissions', '_blank')}
              >
                View All
              </Button>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}